import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PraxisConfig, PraxisConfigInput } from "@shared/praxis-config";
import {
  CreatePraxisResult,
  DomainSetupResult,
  PraxisDetails,
  PraxisOverview,
} from "@shared/praxis-admin";
import { client } from "../../../api/client";

const praxesKey = ["admin", "praxes"] as const;
const praxisKey = (subdomain: string) => [...praxesKey, subdomain] as const;

export const usePraxes = () =>
  useQuery<PraxisOverview[]>({
    queryKey: praxesKey,
    queryFn: async () => (await client.get("/praxis")).data,
  });

export const usePraxis = (subdomain: string) =>
  useQuery<PraxisDetails>({
    queryKey: praxisKey(subdomain),
    queryFn: async () =>
      (await client.get(`/praxis/${encodeURIComponent(subdomain)}`)).data,
  });

export const useDefaultConfig = () =>
  useQuery<PraxisConfig>({
    queryKey: ["admin", "default-config"],
    queryFn: async () => (await client.get("/praxis/default-config")).data,
    staleTime: Infinity,
  });

export type CreatePraxisInput = {
  subdomain: string;
  email: string;
  username: string;
  password: string;
  config: PraxisConfigInput;
  faviconBase64?: string;
};

export const useCreatePraxis = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreatePraxisInput) =>
      (await client.post<CreatePraxisResult>("/praxis", input)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: praxesKey }),
  });
};

export const useUpdatePraxisConfig = (subdomain: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      config: PraxisConfigInput;
      faviconBase64?: string;
    }) => {
      await client.put(
        `/praxis/${encodeURIComponent(subdomain)}/config`,
        input,
      );
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: praxesKey }),
  });
};

export const useUpdatePraxisEmail = (subdomain: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (email: string) => {
      await client.patch(`/praxis/${encodeURIComponent(subdomain)}`, {
        email,
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: praxesKey }),
  });
};

export const useSetupPraxisDomain = (subdomain: string) =>
  useMutation({
    mutationFn: async () =>
      (
        await client.post<DomainSetupResult>(
          `/praxis/${encodeURIComponent(subdomain)}/domain-setup`,
        )
      ).data,
  });
